import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr'
import type { HubConnection, RetryContext } from '@microsoft/signalr'
import { ROOM_HUB_EVENTS } from '../events'
import type { RoomHubHandlers, RoomHubPayloads } from '../events'
import type { RoomConnectionStatus } from '../store/roomStore'
import { isRoomRequestError } from '../transport/roomTransport'
import type { WebinarRoomConnectionDto } from '../types'

/** The longest the page waits between attempts. It never stops trying: a session runs for an hour or more. */
export const MAX_RECONNECT_DELAY_MS = 30_000

/** 1 s, 2 s, 4 s, 8 s, 16 s, then every 30 s for as long as the page is open. */
export function reconnectDelayMs(attempt: number): number {
    return Math.min(MAX_RECONNECT_DELAY_MS, 1000 * 2 ** Math.max(0, attempt))
}

export interface RoomConnectionOptions {
    roomId: string
    /** A fresh `webinar` hub token. Called once for the first connection and once for every reconnect. */
    negotiate: () => Promise<WebinarRoomConnectionDto>
    handlers: Partial<RoomHubHandlers>
    onStatus: (status: RoomConnectionStatus) => void
    /**
     * The link is up, the first time (`recovered` false) and after every drop. Whatever was sent before it joined its
     * groups, or while it was down, never reached it, so reread it.
     */
    onConnected: (recovered: boolean) => void
    /** Builds the hub connection; a test passes a fake. */
    build?: (url: string, accessTokenFactory: () => Promise<string>) => HubConnection
}

export interface RoomConnection {
    start(): Promise<void>
    stop(): Promise<void>
}

function buildHubConnection(url: string, accessTokenFactory: () => Promise<string>): HubConnection {
    return new HubConnectionBuilder()
        .withUrl(url, { accessTokenFactory })
        .withAutomaticReconnect({ nextRetryDelayInMilliseconds: (context: RetryContext) => reconnectDelayMs(context.previousRetryCount) })
        .configureLogging(LogLevel.Warning)
        .build()
}

/**
 * The page's link to the `webinar` hub. The browser connects straight to Azure SignalR with a token our
 * API mints, and the API adds that token's user to the room's groups when it mints it, so every reconnect asks for a
 * fresh token and is put back in its groups on the way.
 *
 * Handlers are registered before the first start, so nothing sent in between is missed. A dropped link retries
 * without end, a closed one is restarted the same way, and only the API refusing the token stops it for good.
 */
export function createRoomConnection(options: RoomConnectionOptions): RoomConnection {
    const build = options.build ?? buildHubConnection
    let connection: HubConnection | null = null
    let heldToken: string | null = null
    let heldAsks = 0
    let everConnected = false
    let stopped = false
    let refused = false
    let attempt = 0
    let retryTimer: ReturnType<typeof setTimeout> | null = null

    const refuse = () => {
        refused = true
        options.onStatus('stopped')
        void connection?.stop()
    }

    const hold = (token: string | null, asks: number) => {
        heldToken = token
        heldAsks = asks
    }
    const dropHeldToken = () => hold(null, 0)

    // The client asks for a token twice per start: for Azure's negotiate, then for the WebSocket. One token from our API
    // serves both, so a (re)connect costs one negotiate there, not two. Each new attempt drops whatever is still held.
    const accessTokenFactory = async (): Promise<string> => {
        if (heldToken !== null && heldAsks > 0) {
            heldAsks--
            return heldToken
        }
        try {
            const token = (await options.negotiate()).AccessToken
            hold(token, 1)
            return token
        } catch (error) {
            if (isRoomRequestError(error, 'not-found')) refuse()
            throw error
        }
    }

    const register = (hub: HubConnection) => {
        for (const event of ROOM_HUB_EVENTS) {
            hub.on(event, (payload: RoomHubPayloads[typeof event] | null) => {
                if (payload?.RoomId !== options.roomId) return
                const handler = options.handlers[event] as ((value: RoomHubPayloads[typeof event]) => void) | undefined
                handler?.(payload)
            })
        }
        hub.onreconnecting(() => {
            dropHeldToken()
            options.onStatus('reconnecting')
        })
        hub.onreconnected(() => {
            options.onStatus('connected')
            options.onConnected(true)
        })
        hub.onclose(() => {
            if (stopped || refused) return
            scheduleAttempt()
        })
    }

    const scheduleAttempt = () => {
        if (stopped || refused || retryTimer !== null) return
        options.onStatus('reconnecting')
        retryTimer = setTimeout(() => {
            retryTimer = null
            void connect()
        }, reconnectDelayMs(attempt++))
    }

    const connect = async () => {
        if (stopped || refused) return
        try {
            if (!connection) {
                const first = await options.negotiate()
                // Stopped while the first negotiate was out: building now would start a link nothing ever stops.
                if (stopped) return
                hold(first.AccessToken, 2)
                connection = build(first.Url, accessTokenFactory)
                register(connection)
            } else {
                dropHeldToken()
            }
            await connection.start()
            if (stopped) {
                await connection.stop()
                return
            }
            const recovered = everConnected
            everConnected = true
            attempt = 0
            options.onStatus('connected')
            options.onConnected(recovered)
        } catch (error) {
            if (isRoomRequestError(error, 'not-found')) {
                if (!refused) refuse()
                return
            }
            if (!refused) scheduleAttempt()
        }
    }

    return {
        async start() {
            if (stopped || connection || retryTimer !== null) return
            options.onStatus('connecting')
            await connect()
        },
        async stop() {
            stopped = true
            if (retryTimer !== null) clearTimeout(retryTimer)
            retryTimer = null
            await connection?.stop()
        },
    }
}
